import type { BatchShapeTransformItem } from '@/lib/transforms/types/batch-shape-transform-item'
import type { ShapeManagerRef } from '@/lib/shapes/types/shape-manager-ref'
import { isShapeLockedForUserEdit } from '@/lib/groups/element-edit-policy'
import type { OnBatchShapesCommitted } from '../types/layout-panel-props'

type CommitShapeBatchOptions =
  | {
      /** Align / Space Between: position-only patch, reported as one 'align' batch. */
      kind: 'position'
    }
  | {
      /** Mirror Move: the full mirrored `after` patch, reported as one 'update' batch. */
      kind: 'mirror'
      historyTxnId?: string
    }

/**
 * Applies `items` to the manager (skipping shapes locked for user edit) and reports the applied
 * ones as a single batch, each carrying its post-update shape.
 */
export function commitShapeBatch(
  mgr: ShapeManagerRef,
  items: BatchShapeTransformItem[],
  onBatchShapesCommitted: OnBatchShapesCommitted,
  options: CommitShapeBatchOptions
): void {
  if (items.length === 0) {
    return
  }

  const shapesById = new Map(mgr.getShapes().map((s) => [s.id, s]))
  const applied: BatchShapeTransformItem[] = []

  for (const item of items) {
    const shape = shapesById.get(item.shapeId)

    if (!shape || isShapeLockedForUserEdit(shape, shapesById)) continue

    if (options.kind === 'mirror') {
      // A group shell's default `updateShape` auto-propagates its box change to descendants via a
      // similarity transform (translate/scale/rotate) — wrong for a reflection. Every descendant is
      // already mirrored independently in `item.after` (see `handleMirrorMove`), so that
      // auto-propagation must be suppressed here or it would re-clobber them right after.
      mgr.updateShape(item.shapeId, item.after, { propagateGroupTransform: false })
    } else {
      mgr.updateShape(item.shapeId, { x: item.after.x, y: item.after.y })
    }

    applied.push(item)
  }

  if (applied.length === 0) {
    return
  }

  const shapesAfter = mgr.getShapes()
  const committed = applied.map((i) => ({
    shapeId: i.shapeId,
    before: i.before,
    after: i.after,
    shape: shapesAfter.find((s) => s.id === i.shapeId),
  }))

  if (options.kind === 'mirror') {
    onBatchShapesCommitted(
      committed,
      'update',
      options.historyTxnId ? { historyTxnId: options.historyTxnId } : undefined
    )
  } else {
    onBatchShapesCommitted(committed, 'align')
  }
}
